import React from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { DepositPaymentType } from '../../../../../../api/types/accounting/deposit-payment-type';
import {
  PendingDepositPaymentsFilterType,
  defaultPendingDepositPaymentsFilter,
} from '../waiting-for-deposit-filter.type';

interface WaitingForDepositFilterProps {
  filters: PendingDepositPaymentsFilterType;
  loading?: boolean;
  onChange: (filters: PendingDepositPaymentsFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

const paymentTypeOptions = [
  { label: 'מזומן', value: DepositPaymentType.CASH },
  { label: 'המחאה', value: DepositPaymentType.CHECK },
];

export const WaitingForDepositFilter: React.FC<
  WaitingForDepositFilterProps
> = ({ filters, loading, onChange, onSearch, onClear }) => {
  const update = <K extends keyof PendingDepositPaymentsFilterType>(
    key: K,
    value: PendingDepositPaymentsFilterType[K],
  ) => {
    onChange({ ...filters, [key]: value });
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
      return;
    }
    onChange(defaultPendingDepositPaymentsFilter());
  };

  const styles = {
    container: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    title: {
      fontSize: '1.1rem',
      fontWeight: 600 as const,
      color: '#333',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
      gap: '12px',
    },
    field: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '4px',
    },
    label: {
      fontSize: '0.8rem',
      color: '#555',
    },
    inputFull: {
      width: '100%',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '12px',
      borderTop: '1px solid #eee',
      paddingTop: '12px',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flexWrap: 'wrap' as const,
    },
    checkboxItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    buttonsRow: {
      display: 'flex',
      gap: '8px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>סינונים</div>

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label}>סוג תשלום</label>
          <MultiSelect
            value={filters.paymentType}
            options={paymentTypeOptions}
            onChange={(e) => update('paymentType', e.value ?? [])}
            placeholder="הכל"
            display="chip"
            style={styles.inputFull}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>תאריך הפקה - מתאריך</label>
          <Calendar
            value={filters.startFromDate}
            onChange={(e) => update('startFromDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="מתאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>תאריך הפקה - עד תאריך</label>
          <Calendar
            value={filters.toDate}
            onChange={(e) => update('toDate', (e.value as Date) ?? null)}
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="עד תאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>תאריך ערך המחאה - מתאריך</label>
          <Calendar
            value={filters.checkDueDateFrom}
            onChange={(e) =>
              update('checkDueDateFrom', (e.value as Date) ?? null)
            }
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="מתאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>תאריך ערך המחאה - עד תאריך</label>
          <Calendar
            value={filters.checkDueDateTo}
            onChange={(e) =>
              update('checkDueDateTo', (e.value as Date) ?? null)
            }
            dateFormat="dd/mm/yy"
            showIcon
            showButtonBar
            placeholder="עד תאריך"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', width: '100%' }}
          />
        </div>
      </div>

      <div style={styles.footer}>
        <div style={styles.checkboxRow}>
          <div style={styles.checkboxItem}>
            <Checkbox
              inputId="shouldReceivePayments"
              checked={filters.shouldReceivePayments}
              onChange={(e) =>
                update('shouldReceivePayments', e.checked ?? false)
              }
            />
            <label
              htmlFor="shouldReceivePayments"
              style={{ cursor: 'pointer' }}
            >
              כלול תשלומי סטודנטים
            </label>
          </div>
          <div style={styles.checkboxItem}>
            <Checkbox
              inputId="shouldReceiveCrm"
              checked={filters.shouldReceiveCrm}
              onChange={(e) => update('shouldReceiveCrm', e.checked ?? false)}
            />
            <label htmlFor="shouldReceiveCrm" style={{ cursor: 'pointer' }}>
              כלול תשלומי CRM
            </label>
          </div>
        </div>

        <div style={styles.buttonsRow}>
          <Button
            icon="pi pi-filter-slash"
            label="נקה"
            text
            severity="secondary"
            onClick={handleClear}
            disabled={loading}
          />
          <Button
            icon="pi pi-search"
            label="חפש"
            onClick={onSearch}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
